import { apiClient } from './api-client';

/**
 * Site-wide AI connection + capability status.
 *
 * Mirrors the PHP AiConnectionController::buildStatus() payload. The credential
 * itself is owned by the shared core library; this is leak-free metadata the SPA
 * uses to decide which AI affordances to show.
 */
export interface AiConnectionStatus {
  /** A credential is stored for the site. */
  connected: boolean;
  /** The ai-assistant provider (master AI toggle) is enabled. */
  enabled: boolean;
  /** THIS user may use AI (enabled + group access + integrations permission). */
  allowed: boolean;
  /** THIS user may connect/disconnect (core.admin). */
  can_connect: boolean;
  subscriber_email: string | null;
  last_validated_at: string | null;
  expires_at: string | null;
  /** The refresh token expired - a reconnect is required. */
  refresh_expired: boolean;
}

/**
 * Credit balance for the connected account (normalized by AICreditsService).
 */
export interface AiCreditsInfo {
  available_credits: number;
  total_credits_purchased: number;
  total_credits_used: number;
  credits_used_percent: number;
  credits_remaining_percent: number;
  expires_at: string | null;
  user_email?: string;
  subscription?: {
    status: string;
    billing_period: string;
    billing_interval: number;
    next_payment_date: string;
  } | null;
}

/**
 * AI Connection Service
 *
 * Single, site-wide connect / disconnect / status surface for the Settings AI
 * panel and every AI feature gate. There is no per-form AI auth anymore.
 */
export const aiConnectionService = {
  /** Cheap capability + connection status (drives feature gating). */
  getStatus(): Promise<AiConnectionStatus> {
    return apiClient.callProxy<AiConnectionStatus>('aiConnection', 'status', {}, 'GET');
  },

  /** Connect the site to the Shondalai AI service (core.admin only). */
  connect(username: string, password: string): Promise<AiConnectionStatus> {
    return apiClient.callProxy<AiConnectionStatus>('aiConnection', 'connect', {
      username,
      password,
    });
  },

  /** Drop the site-wide AI credential (core.admin only). */
  disconnect(): Promise<AiConnectionStatus> {
    return apiClient.callProxy<AiConnectionStatus>('aiConnection', 'disconnect', {});
  },

  /** Current credit balance for the connected account. */
  getCredits(): Promise<AiCreditsInfo> {
    return apiClient.callProxy<AiCreditsInfo>('aiConnection', 'credits', {}, 'GET');
  },
};
